import {type MouseEvent, type TouchEvent, useCallback, useEffect, useRef, useState} from "react";
import {useHistory} from "react-router";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {pluralize} from "@pg-mono/string-utils";

import {hitAiAssistantOffers} from "../../tracking/semantic_search_gtm_events";
import {SemanticSearchFiltersDto} from "../../types/SemanticSearchFiltersDto";
import {SemanticSearchPropertyDto} from "../../types/SemanticSearchPropertyDto";
import {buildSemanticSearchOfferListUrl} from "../../utils/build_semantic_search_offer_list_url";
import {SemanticSearchPropertyListTile} from "./SemanticSearchPropertyListTile";

const offersPluralize = pluralize(["oferta", "oferty", "ofert"]);
const MAX_LISTING_REGIONS = 20;

interface Props {
    properties?: SemanticSearchPropertyDto[][];
    filters?: SemanticSearchFiltersDto;
    count?: number;
}

function flattenProperties(properties: SemanticSearchPropertyDto[][]): SemanticSearchPropertyDto[] {
    return properties.flatMap((group) => group);
}

export function SemanticSearchPropertyList(props: Props) {
    const {properties, filters, count} = props;
    const history = useHistory();
    const listRef = useRef<HTMLUListElement>(null);
    const touchStartRef = useRef<{x: number; y: number} | null>(null);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const items = properties ? flattenProperties(properties) : [];
    const hasMultipleItems = items.length > 1;
    const listingUrl = buildSemanticSearchOfferListUrl(filters);
    const shouldShowListingLink = !!listingUrl && (filters?.regions.length ?? 0) <= MAX_LISTING_REGIONS;
    const listingLinkLabel = typeof count === "number" ? `Zobacz wszystkie ${count} ${offersPluralize(count)}` : "Zobacz wszystkie oferty";

    const handleListingLinkClick = (event: MouseEvent<HTMLElement>) => {
        hitAiAssistantOffers(listingLinkLabel, filters?.regions ?? []);

        const isPlainLeftClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

        if (!listingUrl || event.defaultPrevented || !isPlainLeftClick) return;

        event.preventDefault();
        history.push(listingUrl);
    };

    const updateScrollButtons = useCallback(() => {
        const list = listRef.current;

        if (!list) {
            return;
        }

        setCanScrollPrevious(list.scrollLeft > 0);
        setCanScrollNext(list.scrollWidth > Math.ceil(list.clientWidth + list.scrollLeft));
    }, []);

    useEffect(() => {
        if (!hasMultipleItems) {
            setCanScrollPrevious(false);
            setCanScrollNext(false);
            return;
        }

        const list = listRef.current;

        if (!list) {
            return;
        }

        updateScrollButtons();
        list.addEventListener("scroll", updateScrollButtons);

        const resizeObserver = new ResizeObserver(updateScrollButtons);
        resizeObserver.observe(list);

        return () => {
            list.removeEventListener("scroll", updateScrollButtons);
            resizeObserver.disconnect();
        };
    }, [hasMultipleItems, items.length, updateScrollButtons]);

    if (!properties || properties.length === 0) {
        return null;
    }

    if (items.length === 0) {
        return null;
    }

    const scrollProperties = (direction: -1 | 1) => {
        listRef.current?.scrollBy({
            left: direction * 184,
            behavior: "smooth"
        });
    };

    const handleTouchStart = (event: TouchEvent<HTMLUListElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = {x: touch.clientX, y: touch.clientY};
    };

    const handleTouchMove = (event: TouchEvent<HTMLUListElement>) => {
        const touchStart = touchStartRef.current;

        if (!touchStart || event.touches.length !== 1) {
            return;
        }

        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStart.x);
        const deltaY = Math.abs(touch.clientY - touchStart.y);

        if (deltaX > deltaY) {
            event.stopPropagation();
        }
    };

    const handleTouchEnd = () => {
        touchStartRef.current = null;
    };

    return (
        <div className={propertyListSection}>
            <div className={carouselWrapper}>
                {hasMultipleItems && canScrollPrevious && (
                    <button
                        type="button"
                        aria-label="Poprzednie mieszkania"
                        className={cx(carouselButton, carouselButtonPrevious)}
                        onClick={() => scrollProperties(-1)}
                    >
                        <ChevronLeftIcon size="1.6" />
                    </button>
                )}

                <ul
                    ref={listRef}
                    className={list}
                    aria-label="Mieszkania dopasowane przez Doradcę AI"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    {items.map((property, index) => (
                        <SemanticSearchPropertyListTile key={`${property.id}-${index}`} property={property} />
                    ))}
                </ul>

                {hasMultipleItems && canScrollNext && (
                    <button
                        type="button"
                        aria-label="Następne mieszkania"
                        className={cx(carouselButton, carouselButtonNext)}
                        onClick={() => scrollProperties(1)}
                    >
                        <ChevronRightIcon size="1.6" />
                    </button>
                )}
            </div>

            {shouldShowListingLink && (
                <Button
                    variant="none_secondary"
                    size="small"
                    as="a"
                    href={listingUrl}
                    className={listingLinkButton}
                    iconRight={ChevronRightIcon}
                    onClick={handleListingLinkClick}
                >
                    {listingLinkLabel}
                </Button>
            )}
        </div>
    );
}

const propertyListSection = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const carouselWrapper = css({
    position: "relative",
    borderTop: "1px solid var(--colors-gray-300)",
    pt: 2,
    minWidth: 0
});

const list = css({
    listStyleType: "none",
    m: 0,
    p: 0,
    display: "flex",
    gap: 1.5,
    overflowX: "auto",
    overflowY: "hidden",
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-x",
    overscrollBehaviorX: "contain",
    pr: 2,
    pb: 1,

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const carouselButton = css({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: 2,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    boxShadow: "var(--shadow-1)",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-gray-100)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const carouselButtonPrevious = css({
    left: -1
});

const carouselButtonNext = css({
    right: -1
});

const listingLinkButton = css({
    alignSelf: "flex-start",
    p: 0,
    minHeight: "auto",
    textTransform: "uppercase",
    fontWeight: 700
});
import {type MouseEvent, type TouchEvent, useCallback, useEffect, useRef, useState} from "react";
import {useHistory} from "react-router";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";
import {pluralize} from "@pg-mono/string-utils";

import {hitAiAssistantOffers} from "../../tracking/semantic_search_gtm_events";
import {SemanticSearchFiltersDto} from "../../types/SemanticSearchFiltersDto";
import {SemanticSearchPropertyDto} from "../../types/SemanticSearchPropertyDto";
import {buildSemanticSearchOfferListUrl} from "../../utils/build_semantic_search_offer_list_url";
import {SemanticSearchPropertyListTile} from "./SemanticSearchPropertyListTile";

const offersPluralize = pluralize(["oferta", "oferty", "ofert"]);
const MAX_LISTING_REGIONS = 20;

interface Props {
    properties?: SemanticSearchPropertyDto[][];
    filters?: SemanticSearchFiltersDto;
    count?: number;
}

function flattenProperties(properties: SemanticSearchPropertyDto[][]): SemanticSearchPropertyDto[] {
    return properties.flatMap((group) => group);
}

export function SemanticSearchPropertyList(props: Props) {
    const {properties, filters, count} = props;
    const history = useHistory();
    const listRef = useRef<HTMLUListElement>(null);
    const touchStartRef = useRef<{x: number; y: number} | null>(null);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const items = properties ? flattenProperties(properties) : [];
    const hasMultipleItems = items.length > 1;
    const listingUrl = buildSemanticSearchOfferListUrl(filters);
    const shouldShowListingLink = !!listingUrl && (filters?.regions.length ?? 0) <= MAX_LISTING_REGIONS;
    const listingLinkLabel = typeof count === "number" ? `Zobacz wszystkie ${count} ${offersPluralize(count)}` : "Zobacz wszystkie oferty";

    const handleListingLinkClick = (event: MouseEvent<HTMLElement>) => {
        hitAiAssistantOffers(listingLinkLabel, filters?.regions ?? []);

        const isPlainLeftClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

        if (!listingUrl || event.defaultPrevented || !isPlainLeftClick) return;

        event.preventDefault();
        history.push(listingUrl);
    };

    const updateScrollButtons = useCallback(() => {
        const list = listRef.current;

        if (!list) {
            return;
        }

        setCanScrollPrevious(list.scrollLeft > 0);
        setCanScrollNext(list.scrollWidth > Math.ceil(list.clientWidth + list.scrollLeft));
    }, []);

    useEffect(() => {
        if (!hasMultipleItems) {
            setCanScrollPrevious(false);
            setCanScrollNext(false);
            return;
        }

        const list = listRef.current;

        if (!list) {
            return;
        }

        updateScrollButtons();
        list.addEventListener("scroll", updateScrollButtons);

        const resizeObserver = new ResizeObserver(updateScrollButtons);
        resizeObserver.observe(list);

        return () => {
            list.removeEventListener("scroll", updateScrollButtons);
            resizeObserver.disconnect();
        };
    }, [hasMultipleItems, items.length, updateScrollButtons]);

    if (!properties || properties.length === 0) {
        return null;
    }

    if (items.length === 0) {
        return null;
    }

    const scrollProperties = (direction: -1 | 1) => {
        listRef.current?.scrollBy({
            left: direction * 184,
            behavior: "smooth"
        });
    };

    const handleTouchStart = (event: TouchEvent<HTMLUListElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = {x: touch.clientX, y: touch.clientY};
    };

    const handleTouchMove = (event: TouchEvent<HTMLUListElement>) => {
        const touchStart = touchStartRef.current;

        if (!touchStart || event.touches.length !== 1) {
            return;
        }

        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStart.x);
        const deltaY = Math.abs(touch.clientY - touchStart.y);

        if (deltaX > deltaY) {
            event.stopPropagation();
        }
    };

    const handleTouchEnd = () => {
        touchStartRef.current = null;
    };

    return (
        <div className={propertyListSection}>
            <div className={carouselWrapper}>
                {hasMultipleItems && canScrollPrevious && (
                    <button
                        type="button"
                        aria-label="Poprzednie mieszkania"
                        className={cx(carouselButton, carouselButtonPrevious)}
                        onClick={() => scrollProperties(-1)}
                    >
                        <ChevronLeftIcon size="1.6" />
                    </button>
                )}

                <ul
                    ref={listRef}
                    className={list}
                    aria-label="Mieszkania dopasowane przez Doradcę AI"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    {items.map((property, index) => (
                        <SemanticSearchPropertyListTile key={`${property.id}-${index}`} property={property} />
                    ))}
                </ul>

                {hasMultipleItems && canScrollNext && (
                    <button
                        type="button"
                        aria-label="Następne mieszkania"
                        className={cx(carouselButton, carouselButtonNext)}
                        onClick={() => scrollProperties(1)}
                    >
                        <ChevronRightIcon size="1.6" />
                    </button>
                )}
            </div>

            {shouldShowListingLink && (
                <Button
                    variant="none_secondary"
                    size="small"
                    as="a"
                    href={listingUrl}
                    className={listingLinkButton}
                    iconRight={ChevronRightIcon}
                    onClick={handleListingLinkClick}
                >
                    {listingLinkLabel}
                </Button>
            )}
        </div>
    );
}

const propertyListSection = css({
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    minWidth: 0
});

const carouselWrapper = css({
    position: "relative",
    borderTop: "1px solid var(--colors-gray-300)",
    pt: 2,
    minWidth: 0
});

const list = css({
    listStyleType: "none",
    m: 0,
    p: 0,
    display: "flex",
    gap: 1.5,
    overflowX: "auto",
    overflowY: "hidden",
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    scrollbarWidth: "none",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-x",
    overscrollBehaviorX: "contain",
    pr: 2,
    pb: 1,

    "&::-webkit-scrollbar": {
        display: "none"
    }
});

const carouselButton = css({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: 2,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    boxShadow: "var(--shadow-1)",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-gray-100)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const carouselButtonPrevious = css({
    left: -1
});

const carouselButtonNext = css({
    right: -1
});

const listingLinkButton = css({
    alignSelf: "flex-start",
    p: 0,
    minHeight: "auto",
    textTransform: "uppercase",
    fontWeight: 700
});
